'use client'

import PaypalIcon from '@/assets/icons/paypal.svg'
import VisaIcon from '@/assets/icons/visa.svg'
import { textErrorColor } from '@/core/constants/class-names'
import { useBlockCloseWindow } from '@/core/guards'
import { cn } from '@/core/lib/utils/cn'
import { formatPrice } from '@/core/lib/utils/format-price'
import { FormRadioGroup } from '@/core/ui/forms/radio'
import { PaymentMethod } from '@/features/reservation/types/enums/payment-method.enum'
import { RefundModel } from '@/features/reservation/types/models/refund.model'
import { Divider } from '@heroui/react'
import { useTranslations } from 'next-intl'
import { FormProvider } from 'react-hook-form'
import { PaymentMethodRadio } from './payment-method-radio'
import { PaymentText } from './payment-text'
import { PromoCodeInput } from './promo-code-input'
import { radioClassNames } from './radio-classnames'
import { Refund } from './refund'
import { TermsAndConditionsCheckbox } from './terms-and-conditions-checkbox'
import { useTransferForm } from './use-transfer'
import { WalletOptions } from './wallet-option'

type Props = {
  tripId: string
  refundPromise: Promise<RefundModel>
  isProvider?: boolean
}

export const Transfer: React.FC<Props> = ({
  tripId,
  refundPromise,
  isProvider,
}) => {
  const t = useTranslations()
  const {
    form,
    handleAskForPayment,
    handleConfirmPayment,
    priceDifference,
    shouldShowRestForm,
    shouldConfirmWithWallet,
    walletIncluded,
    user,
    createdTrip,
    tripToUpdate,
    setPaymentDetails,
    paymentDetails,
    paymentButton,
    paymentForm,
    PaymentProvider,
    onPaymentSuccess,
    onPaymentFail,
    totalPrice,
  } = useTransferForm({ tripId, isProvider })

  useBlockCloseWindow({
    hasUnsavedChanges: true,
  })

  if (tripToUpdate && priceDifference < 0)
    return (
      <Refund
        refundPromise={refundPromise}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentFail={onPaymentFail}
      />
    )
  return (
    <article>
      <h2 className='text-2xl font-medium'>{t('trip.transferTitle')}</h2>

      <PaymentProvider
        clientSecret={paymentDetails?.clientSecret}
        currency={createdTrip?.currency.code}
      >
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(
              shouldConfirmWithWallet
                ? handleConfirmPayment
                : handleAskForPayment,
            )}
            className='my-4 flex w-full max-w-lg flex-col gap-2'
          >
            <input {...form.register('tripId')} hidden />
            {priceDifference !== 0 && (
              <FormRadioGroup
                control={form.control}
                name='paymentMethod'
                className='w-full max-w-full'
                onSelectValue={() => {
                  setPaymentDetails(null)
                }}
              >
                {user && (
                  <WalletOptions
                    user={user}
                    createdTrip={createdTrip}
                    shouldShowRestForm={shouldShowRestForm}
                    walletIncluded={walletIncluded}
                  />
                )}

                <PaymentMethodRadio
                  color='danger'
                  classNames={radioClassNames()}
                  value={PaymentMethod.BANK_CARD}
                  bordered
                >
                  <VisaIcon className='text-navy h-8 w-8' />
                  {t('forms.bank')}
                </PaymentMethodRadio>

                <PaymentMethodRadio
                  color='danger'
                  classNames={radioClassNames()}
                  value={PaymentMethod.PAYPAL}
                  bordered
                >
                  <PaypalIcon className='text-navy h-8 w-8' />
                  {t('forms.paypal')}
                </PaymentMethodRadio>
              </FormRadioGroup>
            )}
            {form.formState.errors.paymentMethod && (
              <p className={cn('text-sm', textErrorColor)}>
                {form.formState.errors.paymentMethod.message?.toString()}
              </p>
            )}

            {paymentForm}

            <div className='mt-2 mb-4'>
              <TermsAndConditionsCheckbox />
            </div>
            <Divider />
            <div className='mt-2 mb-4'>
              <PromoCodeInput tripId={tripId} />
            </div>

            <PaymentText />
            <div className='flex flex-wrap items-center justify-start gap-5'>
              <p className='flex flex-col'>
                <span className='text-grey'>{t('trip.totalInc')}</span>
                <span className='text-red text-2xl leading-8 font-semibold min-[28rem]:text-4xl'>
                  {formatPrice(totalPrice, createdTrip?.currency)}
                </span>
              </p>
              {paymentButton}
            </div>
          </form>
        </FormProvider>
      </PaymentProvider>
    </article>
  )
}
